import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import ReactCrop, { type Crop, type PercentCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { InvoiceLogoCropPayload } from "@/services/authService";

/**
 * Mirrors the backend contract in `common/invoice-logo-crop.ts`. Kept in sync
 * by hand because the portal has no generated client; the API re-validates
 * everything, so a drift here is a rejected save, never a bad render.
 */
const MIN_SIZE_PT = 40;
const MAX_SIZE_PT = 160;
const MIN_CROP_EXTENT_PCT = 1; // 1% — the renderer divides by this
/** Pre-feature size; shown as 100% so the readout is relative to "as before". */
const DEFAULT_SIZE_PT = 76;

/** The PDF fits the crop into a square box of `sizePt`; the preview mimics it. */
const PREVIEW_BOX_PX = 132;

export interface InvoiceLogoCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Original (uncropped) logo URL. */
  imageUrl: string;
  initial: InvoiceLogoCropPayload;
  saving: boolean;
  /** True when the image is a not-yet-uploaded local file, which changes the
   *  confirm copy from "save adjustments" to "upload". */
  isNewUpload?: boolean;
  onSave: (payload: InvoiceLogoCropPayload) => void;
}

export function InvoiceLogoCropDialog({
  open,
  onOpenChange,
  imageUrl,
  initial,
  saving,
  isNewUpload = false,
  onSave,
}: InvoiceLogoCropDialogProps): JSX.Element {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement | null>(null);

  // react-image-crop speaks percent (0..100); the API speaks fractions (0..1).
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    x: initial.invoiceLogoCropX * 100,
    y: initial.invoiceLogoCropY * 100,
    width: initial.invoiceLogoCropW * 100,
    height: initial.invoiceLogoCropH * 100,
  });
  const [sizePt, setSizePt] = useState(initial.invoiceLogoSizePt);
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);

  // Re-seed ONLY on the closed -> open transition, so cancelling and reopening
  // does not show stale drag state.
  //
  // Deliberately NOT keyed on `initial`: callers build it as an inline object
  // literal, so its reference changes on every parent render. Depending on it
  // made the editor snap back to the stored values mid-save (the parent
  // re-renders when its `saving` flag flips) — the save was correct, but the UI
  // appeared to discard the user's drag. Read through a ref so the seed values
  // are current without the identity churn driving the effect.
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    const seed = initialRef.current;
    setCrop({
      unit: "%",
      x: seed.invoiceLogoCropX * 100,
      y: seed.invoiceLogoCropY * 100,
      width: seed.invoiceLogoCropW * 100,
      height: seed.invoiceLogoCropH * 100,
    });
    setSizePt(seed.invoiceLogoSizePt);
  }, [open]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    setNaturalRatio(naturalWidth / naturalHeight);
  }, []);

  /** Guards the same degenerate crop the backend rejects, before the round-trip. */
  const tooSmall = crop.width < MIN_CROP_EXTENT_PCT || crop.height < MIN_CROP_EXTENT_PCT;

  /**
   * Live preview of the PDF result: the cropped region fitted into a square box,
   * exactly as `invoice-preview-pdf.service` does it. Implemented with a scaled
   * background image rather than a canvas so it stays cheap on every drag frame.
   */
  const previewStyle = useMemo(() => {
    if (!naturalRatio || tooSmall) return null;
    const cw = crop.width / 100;
    const chh = crop.height / 100;
    const cropAspect = (cw * naturalRatio) / chh;
    // The size control must be VISIBLE here or it reads as broken: scale the
    // box by sizePt against the maximum, inside a fixed frame representing the
    // largest allowed logo, so dragging the slider visibly grows and shrinks
    // the logo the way it will on the PDF.
    const sizeBox = PREVIEW_BOX_PX * (sizePt / MAX_SIZE_PT);
    let boxW = sizeBox;
    let boxH = sizeBox;
    if (cropAspect >= 1) boxH = sizeBox / cropAspect;
    else boxW = sizeBox * cropAspect;
    return {
      width: `${boxW}px`,
      height: `${boxH}px`,
      backgroundImage: `url(${imageUrl})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: `${boxW / cw}px ${boxH / chh}px`,
      backgroundPosition: `-${(crop.x / 100) * (boxW / cw)}px -${(crop.y / 100) * (boxH / chh)}px`,
    } as const;
  }, [crop, naturalRatio, imageUrl, tooSmall, sizePt]);

  const handleSave = (): void => {
    onSave({
      invoiceLogoCropX: crop.x / 100,
      invoiceLogoCropY: crop.y / 100,
      invoiceLogoCropW: crop.width / 100,
      invoiceLogoCropH: crop.height / 100,
      invoiceLogoSizePt: sizePt,
    });
  };

  const handleReset = (): void => {
    setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
    setSizePt(DEFAULT_SIZE_PT);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {isNewUpload
              ? t("settings.companyImage.crop.titleNew")
              : t("settings.companyImage.crop.title")}
          </DialogTitle>
          <DialogDescription>
            {isNewUpload
              ? t("settings.companyImage.crop.descriptionNew")
              : t("settings.companyImage.crop.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-[1fr_auto]">
          <div className="flex min-w-0 items-center justify-center rounded-lg bg-[var(--fx-paper-2)] p-2">
            <ReactCrop
              crop={crop}
              onChange={(_px, percent: PercentCrop) => setCrop(percent)}
              keepSelection
              minWidth={8}
              minHeight={8}
              className="max-h-[340px] [&_img]:max-h-[340px] [&_img]:w-auto"
            >
              <img
                ref={imgRef}
                src={imageUrl}
                onLoad={onImageLoad}
                alt={t("settings.companyImage.crop.imageAlt", "Logo")}
              />
            </ReactCrop>
          </div>

          <div className="flex w-full flex-col gap-4 md:w-[190px]">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("settings.companyImage.crop.previewLabel")}
              </div>
              {/* Fixed frame = the largest allowed logo, so the size control
                  has a stable reference to grow and shrink against. */}
              <div
                className="flex items-center justify-center rounded-lg border bg-[var(--fx-paper)] p-3"
                style={{ height: PREVIEW_BOX_PX + 24, width: PREVIEW_BOX_PX + 24 }}
              >
                {previewStyle ? (
                  <div style={previewStyle} />
                ) : (
                  <span className="text-center text-xs text-muted-foreground">
                    {t("settings.companyImage.crop.previewEmpty")}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="invoice-logo-size"
                className="mb-1 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                <span>{t("settings.companyImage.crop.sizeLabel")}</span>
                {/* Numeric feedback: without it the slider looks inert on a
                    logo whose aspect makes the size change subtle. */}
                <span className="font-mono normal-case tracking-normal text-foreground">
                  {Math.round((sizePt / DEFAULT_SIZE_PT) * 100)}%
                </span>
              </label>
              <input
                id="invoice-logo-size"
                type="range"
                min={MIN_SIZE_PT}
                max={MAX_SIZE_PT}
                step={2}
                value={sizePt}
                onChange={(e) => setSizePt(Number(e.target.value))}
                className="w-full accent-[var(--fx-accent-500)]"
              />
              <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
                <span>{t("settings.companyImage.crop.sizeSmall")}</span>
                <span>{t("settings.companyImage.crop.sizeLarge")}</span>
              </div>
            </div>

            <Button type="button" variant="outline" size="sm" onClick={handleReset}>
              {t("settings.companyImage.crop.reset")}
            </Button>
          </div>
        </div>

        {tooSmall && (
          <p className="text-sm text-[var(--fx-warn-text)]">
            {t("settings.companyImage.crop.tooSmall")}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || tooSmall}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isNewUpload
              ? t("settings.companyImage.crop.saveNew")
              : t("settings.companyImage.crop.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
